import type { FC, PropsWithChildren } from 'react'
import { createContext, useContext, useEffect, useRef, useState } from 'react'

type MenuItem = {
  element: HTMLDivElement
  label: string
  isVisible?: boolean
}

type MenuContextType = {
  menuItems: MenuItem[]
  addMenuItem: (item: MenuItem) => void
}

const MenuContext = createContext<MenuContextType | null>(null)

export const MenuContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const [visibleSection, setVisibleSection] = useState<HTMLDivElement | null>(
    null
  )

  const createObserver = () =>
    new IntersectionObserver(
      entries => {
        const visibleEntry = entries.find(entry => entry.isIntersecting)

        if (visibleEntry) {
          setVisibleSection(visibleEntry.target as HTMLDivElement)
        }
      },
      { rootMargin: '-30% 0% -70% 0%' }
    )

  const observer = useRef<IntersectionObserver>(createObserver())

  useEffect(() => {
    if (!observer.current) {
      observer.current = createObserver()
    }

    return () => {
      setMenuItems([])
      observer.current?.disconnect()
    }
  }, [])

  const addMenuItem = (item: MenuItem) => {
    setMenuItems(v => [...v, item])
    observer.current?.observe(item.element)
  }

  return (
    <MenuContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        menuItems: menuItems.map(item => ({
          ...item,
          isVisible: item.element === visibleSection,
        })),
        addMenuItem,
      }}
    >
      {children}
    </MenuContext.Provider>
  )
}

export const useMenuContext = () => {
  return useContext(MenuContext) as MenuContextType
}
